import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Heart, 
  Calendar, 
  Clock, 
  Shield, 
  Car, 
  Users, 
  Phone, 
  MapPin,
  CheckCircle,
  Star,
  Stethoscope,
  Accessibility,
  CalendarCheck,
  ArrowRight
} from "lucide-react";
import ServiceFAQ from "@/components/ServiceFAQ";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import bamcHeroImage from "@/assets/bamc-hero.jpg";

const BAMCTransportation = () => {
  const faqs = [
    {
      question: "Can Rick's Black Car Service enter Fort Sam Houston for BAMC appointments?",
      answer: "Yes! Rick's Black Car Service has verified base access credentials (DBIDS) that allow our chauffeurs to enter Fort Sam Houston without a sponsor. We can drop you off and pick you up directly at BAMC's main entrance or any other location on base."
    },
    {
      question: "How early should I book transportation for a BAMC appointment?",
      answer: "We recommend booking at least 24-48 hours in advance. For recurring appointments, we can set up a regular schedule. Last-minute bookings may be available—call us directly at (210) 296-7409 to check availability."
    },
    {
      question: "What information do you need for a BAMC pick-up or drop-off?",
      answer: "We require the full legal name and date of birth for all passengers at booking. All passengers over 16 must have a valid government-issued photo ID (CAC, Driver's License, or Passport) ready to present at the gate."
    },
    {
      question: "Can you wait during my medical appointment?",
      answer: "Absolutely! Our hourly service ($100/hour, 3-hour minimum) is perfect for medical appointments with uncertain duration. Your chauffeur will wait on-site and be ready to take you home whenever your appointment concludes."
    },
    {
      question: "Do you accommodate patients with mobility needs?",
      answer: "Yes, we can accommodate most mobility needs. Our luxury vehicles have spacious interiors for comfortable entry and exit. Please let us know about any specific requirements when booking so we can ensure the best possible experience."
    },
    {
      question: "What if my appointment runs late or is rescheduled?",
      answer: "We understand medical appointments can be unpredictable. Just contact us as soon as possible if your appointment runs late or is rescheduled. We pride ourselves on flexibility and will work with you to adjust your transportation accordingly."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "BAMC Medical Appointment Transportation Service",
    "description": "Professional luxury transportation to Brooke Army Medical Center (BAMC), the Department of Defense's largest inpatient medical facility at Fort Sam Houston. Verified base access, DBIDS-credentialed chauffeurs.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "Fort Sam Houston / BAMC, San Antonio, TX"
    },
    "serviceType": "Medical Appointment Transportation"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ricksblackcarservicesantonio.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://ricksblackcarservicesantonio.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Military Transportation",
        "item": "https://ricksblackcarservicesantonio.com/jbsa-military-transportation"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "BAMC Transportation",
        "item": "https://ricksblackcarservicesantonio.com/bamc-transportation"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>BAMC Transportation | Fort Sam Houston Medical Appointments</title>
        <meta 
          name="description" 
          content="Professional transportation to BAMC at Fort Sam Houston. Verified base access, DBIDS-credentialed chauffeurs. Book your medical transport today." 
        />
        <meta name="keywords" content="BAMC transportation, Fort Sam Houston car service, Brooke Army Medical Center, medical appointment transportation San Antonio, military hospital transport, DoD medical facility" />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/bamc-transportation" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url(${bamcHeroImage})`,
              backgroundPosition: 'center'
            }}
          />
          <div className="relative z-10 container mx-auto px-4 text-center">
            <Breadcrumb 
              items={[
                { name: "Services", url: "/services" },
                { name: "Military Transportation", url: "/services/military-transportation" },
                { name: "BAMC Transportation", url: "/bamc-transportation" }
              ]} 
            />
            <div className="flex justify-center mb-6">
              <div className="bg-primary/20 p-4 rounded-full">
                <Heart className="h-16 w-16 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              BAMC Medical Appointment Transportation
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              Reliable, stress-free transportation to Brooke Army Medical Center
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Medical Transport</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg bg-background/10 border-white text-white hover:bg-white hover:text-foreground">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-card border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Shield className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Verified Base Access</span>
              </div>
              <div className="flex flex-col items-center">
                <CheckCircle className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">DBIDS Credentialed</span>
              </div>
              <div className="flex flex-col items-center">
                <Car className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Luxury Audi Fleet</span>
              </div>
              <div className="flex flex-col items-center">
                <Star className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">5-Star Service</span>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Medical Patients Choose Us</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Medical appointments are stressful enough. Let us handle the transportation so you can focus on your health.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <Card className="border-primary/20">
                <CardContent className="p-6 text-center">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Stethoscope className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Direct BAMC Access</h3>
                  <p className="text-muted-foreground text-sm">
                    We drop you off right at BAMC's entrance—no parking hassles, no long walks from distant lots.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20">
                <CardContent className="p-6 text-center">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Wait Time Available</h3>
                  <p className="text-muted-foreground text-sm">
                    Need us to wait? Our hourly service means your chauffeur stays on-site until your appointment is done.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20">
                <CardContent className="p-6 text-center">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Accessibility className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Comfort First</h3>
                  <p className="text-muted-foreground text-sm">
                    Spacious luxury vehicles with easy entry/exit. Perfect for patients needing extra comfort.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <Link to="/blog/what-is-jbsa-access-guide" className="text-primary hover:underline font-medium inline-flex items-center gap-2">
                Learn About JBSA Access Requirements
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* BAMC Information */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">About BAMC</h2>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Brooke Army Medical Center (BAMC) at Fort Sam Houston is the Department of Defense's largest inpatient medical facility, serving active duty military, retirees, and their families.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="border-primary/20">
                <CardHeader className="bg-primary/5">
                  <CardTitle className="flex items-center gap-3">
                    <MapPin className="h-6 w-6 text-primary" />
                    Location & Hours
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Address</p>
                      <p className="text-sm text-muted-foreground">3551 Roger Brooke Dr, Fort Sam Houston, TX 78234</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Main Hospital</p>
                      <p className="text-sm text-muted-foreground">Open 24/7 for emergency services</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CalendarCheck className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Outpatient Clinics</p>
                      <p className="text-sm text-muted-foreground">Typically Mon-Fri, hours vary by department</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20">
                <CardHeader className="bg-primary/5">
                  <CardTitle className="flex items-center gap-3">
                    <Heart className="h-6 w-6 text-primary" />
                    Services at BAMC
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Specialty Care</p>
                      <p className="text-sm text-muted-foreground">World-renowned burn center and trauma care</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Rehabilitation</p>
                      <p className="text-sm text-muted-foreground">Center for the Intrepid rehabilitation facility</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Outpatient Care</p>
                      <p className="text-sm text-muted-foreground">Primary care, dental, pharmacy, and more</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Transportation Packages */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">BAMC Transportation Packages</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Transparent pricing based on distance & time. 20% gratuity always included. No hidden fees.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Airport Package */}
              <Card className="relative overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    SAT Airport
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-2xl font-bold text-primary">~7 miles</span>
                    <p className="text-sm text-muted-foreground">to Fort Sam Houston/BAMC</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flight monitoring
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Meet & greet service
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/quick-quote">Get Instant Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Downtown Package */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Downtown/Riverwalk
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-2xl font-bold text-primary">~5 miles</span>
                    <p className="text-sm text-muted-foreground">to Fort Sam Houston/BAMC</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Hotel pickup available
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flexible timing
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/quick-quote">Get Instant Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Hourly Package */}
              <Card className="border-primary relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-bl-lg font-semibold">
                  BEST FOR APPOINTMENTS
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    Hourly Service
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-4xl font-bold text-primary">$85</span>
                    <span className="text-muted-foreground">/hour</span>
                    <p className="text-sm text-muted-foreground">3-hour minimum</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Dedicated chauffeur
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Wait time included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Round-trip service
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Hourly Service</a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Pricing Disclaimer */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border/50 max-w-5xl mx-auto">
              <p className="text-sm text-muted-foreground text-center">
                <span className="font-medium text-foreground">Note:</span> Fares shown are estimates and calculated dynamically based on distance and time. 
                Actual fare may vary slightly depending on your exact pickup location. Use our{' '}
                <Link to="/request-quote" className="text-primary hover:underline font-medium">Instant Quote Calculator</Link>{' '}
                for a precise quote.
              </p>
            </div>

            <div className="text-center mt-8">
              <p className="text-muted-foreground mb-4">Need recurring appointment transportation?</p>
              <Button asChild variant="outline">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-4 w-4" />
                  Call for Regular Schedule Rates
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">What to Expect</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our seamless process ensures you arrive relaxed and on time
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-4 items-start">
                  <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Book Your Ride</h3>
                    <p className="text-muted-foreground text-sm">
                      Schedule online or call us with your appointment details. We'll confirm timing and provide passenger information requirements.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Comfortable Pickup</h3>
                    <p className="text-muted-foreground text-sm">
                      Your chauffeur arrives early in a luxury Audi. Relax in climate-controlled comfort with complimentary water.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Direct Base Entry</h3>
                    <p className="text-muted-foreground text-sm">
                      We handle gate security with our DBIDS credentials. You'll be dropped off directly at BAMC's entrance.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Ready When You Are</h3>
                    <p className="text-muted-foreground text-sm">
                      For hourly service, your chauffeur waits on-site. Or schedule a return pickup—we'll be there right on time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Important Information for Patients</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-primary/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Shield className="h-5 w-5 text-primary" />
                      Required Documentation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>• Valid government-issued photo ID for all passengers over 16</p>
                    <p>• CAC (Common Access Card) if applicable</p>
                    <p>• Full legal name and DOB provided at booking</p>
                    <p>• Medical appointment confirmation (recommended)</p>
                  </CardContent>
                </Card>

                <Card className="border-primary/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="h-5 w-5 text-primary" />
                      Timing Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>• Schedule pickup 45-60 minutes before appointment</p>
                    <p>• Allow extra time for base security checks</p>
                    <p>• Early morning appointments may have lighter gate traffic</p>
                    <p>• Contact us immediately if appointment time changes</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <ServiceFAQ faqs={faqs} title="BAMC Transportation FAQs" />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Book Your Medical Transport?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Let us take the stress out of getting to your BAMC appointment. Professional, reliable, and comfortable transportation with verified base access.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Now</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </>
  );
};

export default BAMCTransportation;
